import React, { useState, useEffect, useMemo } from "react";
import { Table } from "antd";
import TableTitle from "../../../../../../common/TableTitle";
import { SearchHeader } from "../../../../../../layout/header/HeaderList";

const SigTab = ({ sigData = [], chargement, mission = {} }) => {
  // États
  const [filtersItems, setFiltersItems] = useState([]);
  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 100,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total, range) =>
        `${range[0]}-${range[1]} sur ${total} éléments`,
    },
  });

  // Initialisation des données filtrées
  useEffect(() => {
    setFiltersItems(sigData);
  }, [sigData]);

  // Fonctions utilitaires
  const calculateVariation = (montantN, montantN1) => {
    if (!montantN1 || montantN1 === 0) return 0;
    return ((montantN - montantN1) / montantN1) * 100;
  };

  const getVariationClass = (taux) => {
    const tauxAbs = Math.abs(taux);
    if (tauxAbs >= 10) return "bg-danger text-white";
    if (tauxAbs >= 5) return "bg-warning text-dark";
    return "";
  };

  const formatMontant = (montant) => {
    return (montant || 0).toLocaleString("fr");
  };

  // ✅ Configuration des colonnes personnalisées comme BilanActifEA
  const columns = useMemo(
    () => [
      {
        title: () => <TableTitle>#</TableTitle>,
        render: (_, record, index) => (
          <span className="text-muted fw-semibold">{index + 1}</span>
        ),
        width: 50,
        fixed: "left",
      },
      {
        title: () => <TableTitle>Référence</TableTitle>,
        dataIndex: "ref",
        width: 80,
        fixed: "left",
        render: (_, record) => (
          <span className="fw-bold text-primary" style={{ fontSize: "14px" }}>
            {record.ref}
          </span>
        ),
      },
      {
        title: () => <TableTitle>Libellé</TableTitle>,
        dataIndex: "libelle",
        width: 350,
        render: (_, record) => (
          <div className="d-flex flex-column">
            <span className="fw-bold" style={{ fontSize: "14px" }}>
              {record.libelle}
            </span>
            {record.description && (
              <span className="text-muted fs-7">{record.description}</span>
            )}
          </div>
        ),
        ellipsis: true,
      },
      {
        title: () => <TableTitle>Exercice {mission.annee || "N"}</TableTitle>,
        dataIndex: "montant",
        width: 130,
        align: "right",
        render: (_, record) => (
          <div className="text-end fw-bold" style={{ fontSize: "14px" }}>
            {formatMontant(record.montant)}
          </div>
        ),
      },
      {
        title: () => (
          <TableTitle>
            Exercice {parseInt(mission.annee) - 1 || "N-1"}
          </TableTitle>
        ),
        dataIndex: "montantN1",
        width: 130,
        align: "right",
        render: (_, record) => (
          <div className="text-end fw-bold" style={{ fontSize: "14px" }}>
            {formatMontant(record.montantN1)}
          </div>
        ),
      },
      {
        title: () => <TableTitle>N - N-1</TableTitle>,
        align: "center",
        children: [
          {
            title: () => <TableTitle>Variation</TableTitle>,
            width: 130,
            align: "right",
            render: (_, record) => {
              const variation = record.montant - (record.montantN1 || 0);
              const taux = calculateVariation(record.montant, record.montantN1);

              return (
                <div
                  className={`text-end p-2 fw-bold rounded ${getVariationClass(
                    taux
                  )}`}
                  style={{ fontSize: "13px" }}
                >
                  {formatMontant(variation)}
                </div>
              );
            },
          },
          {
            title: () => <TableTitle>Taux (%)</TableTitle>,
            width: 100,
            align: "right",
            render: (_, record) => {
              const taux = calculateVariation(record.montant, record.montantN1);

              return (
                <div
                  className={`text-end p-2 fw-bold rounded ${getVariationClass(
                    taux
                  )}`}
                  style={{ fontSize: "13px" }}
                >
                  {taux.toFixed(2)}%
                </div>
              );
            },
          },
        ],
      },
      {
        title: () => (
          <TableTitle>
            Exercice {parseInt(mission.annee) - 1 || "N-1"}
          </TableTitle>
        ),
        dataIndex: "montantN1",
        width: 130,
        align: "right",
        render: (_, record) => (
          <div className="text-end fw-bold" style={{ fontSize: "14px" }}>
            {formatMontant(record.montantN1)}
          </div>
        ),
      },
      {
        title: () => (
          <TableTitle>
            Exercice {parseInt(mission.annee) - 2 || "N-2"}
          </TableTitle>
        ),
        dataIndex: "montantN2",
        width: 130,
        align: "right",
        render: (_, record) => (
          <div
            className="text-end fw-bold text-muted"
            style={{ fontSize: "14px" }}
          >
            {formatMontant(record.montantN2 || 0)}
          </div>
        ),
      },
      {
        title: () => <TableTitle>N-1 - N-2</TableTitle>,
        align: "center",
        children: [
          {
            title: () => <TableTitle>Variation</TableTitle>,
            width: 130,
            align: "right",
            render: (_, record) => {
              const variation =
                (record.montantN1 || 0) - (record.montantN2 || 0);
              const taux = calculateVariation(
                record.montantN1,
                record.montantN2
              );

              return (
                <div
                  className={`text-end p-2 fw-bold rounded ${getVariationClass(
                    taux
                  )}`}
                  style={{ fontSize: "13px" }}
                >
                  {formatMontant(variation)}
                </div>
              );
            },
          },
          {
            title: () => <TableTitle>Taux (%)</TableTitle>,
            width: 100,
            align: "right",
            render: (_, record) => {
              const taux = calculateVariation(
                record.montantN1,
                record.montantN2
              );

              return (
                <div
                  className={`text-end p-2 fw-bold rounded ${getVariationClass(
                    taux
                  )}`}
                  style={{ fontSize: "13px" }}
                >
                  {taux.toFixed(2)}%
                </div>
              );
            },
          },
        ],
      },
    ],
    [mission.annee]
  );

  // Gestionnaires d'événements
  const handleSearchChange = (e) => {
    const searchTerm = e.target.value.toLowerCase().trim();

    if (!searchTerm) {
      setFiltersItems(sigData);
      return;
    }

    const filtered = sigData.filter((item) => {
      const libelleMatch = item.libelle?.toLowerCase().includes(searchTerm);
      const refMatch = item.ref?.toString().toLowerCase().includes(searchTerm);
      return libelleMatch || refMatch;
    });

    setFiltersItems(filtered);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      sorter,
    });
  };

  // Calcul des totaux
  const totals = useMemo(() => {
    return filtersItems.reduce(
      (acc, item) => ({
        montant: acc.montant + (item.montant || 0),
        montantN1: acc.montantN1 + (item.montantN1 || 0),
        montantN2: acc.montantN2 + (item.montantN2 || 0),
      }),
      { montant: 0, montantN1: 0, montantN2: 0 }
    );
  }, [filtersItems]);

  // Si pas de données
  if (!sigData.length) {
    return (
      <div className="text-center py-5">
        <div className="text-muted">
          <i className="fas fa-chart-bar fa-3x mb-3"></i>
          <p className="fs-5">Aucune donnée disponible pour le SIG</p>
        </div>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <div style={{ maxWidth: "300px", width: "100%" }}>
        <SearchHeader
          placeholder="Rechercher par référence ou libellé..."
          handleSearch={handleSearchChange}
        />
      </div>

      {/* Tableau principal */}
      <div className="py-4">
        <Table
          columns={columns}
          rowKey={(record, index) => record.id || `${record.ref}-${index}`}
          dataSource={filtersItems}
          pagination={{
            ...tableParams.pagination,
            size: "small",
          }}
          onChange={handleTableChange}
          loading={chargement}
          size="small"
          scroll={{ x: 1200, y: 800 }}
          rowClassName={(record, index) => {
            const baseClass = "cursor-pointer transition-all";
            const colorClass = record.color ? `bg-${record.color}` : "";
            const hoverClass = index % 2 === 0 ? "bg-light-hover" : "";
            return `${baseClass} ${colorClass} ${hoverClass}`;
          }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row className="bg-light fw-bold">
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>TOTAL</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <strong>{formatMontant(totals.montant)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <strong>{formatMontant(totals.montantN1)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <strong>
                    {formatMontant(totals.montant - totals.montantN1)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <strong>
                    {calculateVariation(
                      totals.montant,
                      totals.montantN1
                    ).toFixed(2)}
                    %
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <strong>{formatMontant(totals.montantN1)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  <strong>{formatMontant(totals.montantN2)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right">
                  <strong>
                    {formatMontant(totals.montantN1 - totals.montantN2)}
                  </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} align="right">
                  <strong>
                    {calculateVariation(
                      totals.montantN1,
                      totals.montantN2
                    ).toFixed(2)}
                    %
                  </strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
          locale={{
            emptyText: "Aucun élément trouvé",
          }}
        />
      </div>
    </div>
  );
};

export { SigTab };
